import {
  NOTE_PREVIEW_ADDENDUM_SECTION_KEY,
  NOTE_PREVIEW_COSIGN_SECTION_KEY,
  NOTE_PREVIEW_LABELS,
  NOTE_PREVIEW_PLAN_OF_CARE_SECTION_KEY,
  NOTE_PREVIEW_PROCEDURE_CODES_SECTION_KEY,
  NOTE_PREVIEW_PROCEDURE_CODES_TITLE,
  NOTE_PREVIEW_PROCEDURES_SECTION_PATTERN,
} from "./note-preview-constant";
import { isFreeTextTitle } from "./note-preview-line-utils";
import {
  NotePreviewLine,
  NotePreviewSection,
  NotePreviewView,
} from "./note-preview-types";

const isProceduresSection = (section: NotePreviewSection) =>
  NOTE_PREVIEW_PROCEDURES_SECTION_PATTERN.test(section.key) ||
  NOTE_PREVIEW_PROCEDURES_SECTION_PATTERN.test(section.title);

const isCptLine = (line: NotePreviewLine) =>
  !!line.title && NOTE_PREVIEW_PROCEDURES_SECTION_PATTERN.test(line.title);

const isCptNoteLine = (line: NotePreviewLine) =>
  !!line.title && isFreeTextTitle(line.title);

/** Prints the Procedures (CPT) table as the last section, "Procedure Codes", as the editor places it after the assessments. */
export const moveProceduresToEnd = (
  sections: NotePreviewSection[]
): NotePreviewSection[] => {
  const planOfCare = sections.find(
    (section) => section.key === NOTE_PREVIEW_PLAN_OF_CARE_SECTION_KEY
  );
  if (!planOfCare) return sections;
  const procedures = sections.find(isProceduresSection);
  const cptLines = planOfCare.lines.filter(isCptLine);
  // Plan of Care's only section-level free text is the CPT box's "Additional note".
  const cptNoteLines = planOfCare.lines.filter(isCptNoteLine);
  if (!procedures && !cptLines.length && !cptNoteLines.length) return sections;

  const planLines = planOfCare.lines.filter(
    (line) => !isCptLine(line) && !isCptNoteLine(line)
  );
  // The table already lists the picked codes, so the plan's CPT lines are only kept when it is missing.
  const procedureLines = [
    ...(procedures?.lines ?? []),
    ...(procedures?.table ? [] : cptLines),
    ...cptNoteLines,
  ];
  const rest = sections
    .filter((section) => section !== procedures)
    .map((section) =>
      section === planOfCare
        ? { ...section, lines: planLines, isAnswered: planLines.length > 0 }
        : section
    )
    .filter((section) => section.isAnswered);
  return [
    ...rest,
    {
      key: procedures?.key ?? NOTE_PREVIEW_PROCEDURE_CODES_SECTION_KEY,
      group: procedures?.group ?? planOfCare.group,
      table: procedures?.table,
      title: NOTE_PREVIEW_PROCEDURE_CODES_TITLE,
      lines: procedureLines,
      isAnswered: true,
    },
  ];
};

const SIGNED_BLOCK_KEYS: readonly string[] = [
  NOTE_PREVIEW_ADDENDUM_SECTION_KEY,
  NOTE_PREVIEW_COSIGN_SECTION_KEY,
];

const signedBlockSection = (
  key: string,
  title: string
): NotePreviewSection => ({
  key,
  title,
  group: key,
  lines: [],
  isAnswered: true,
});

/** Addendum and co-sign as "Include in report" entries, only when the note has them. */
export const buildSignedBlockSections = (
  view: NotePreviewView
): NotePreviewSection[] => [
  ...(view.cosign
    ? [
        signedBlockSection(
          NOTE_PREVIEW_COSIGN_SECTION_KEY,
          NOTE_PREVIEW_LABELS.cosignedNote
        ),
      ]
    : []),
  ...(view.addendum
    ? [
        signedBlockSection(
          NOTE_PREVIEW_ADDENDUM_SECTION_KEY,
          NOTE_PREVIEW_LABELS.addendum
        ),
      ]
    : []),
];

export const isSignedBlockSection = (section: NotePreviewSection): boolean =>
  SIGNED_BLOCK_KEYS.includes(section.key);

/** Drops the addendum / co-sign the user unticked so the paper and the PDF match the panel. */
export const applySignedBlockSelection = (
  view: NotePreviewView,
  isSelected: (key: string) => boolean
): NotePreviewView => ({
  ...view,
  addendum: isSelected(NOTE_PREVIEW_ADDENDUM_SECTION_KEY)
    ? view.addendum
    : undefined,
  cosign: isSelected(NOTE_PREVIEW_COSIGN_SECTION_KEY) ? view.cosign : undefined,
});
